import { logger, sanitizeError } from '@heidi/runtime/logger';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useAtom } from 'jotai';
import { useCallback } from 'react';

import { agentHarnessKeys, inboxQueryKeys } from '../../../contracts/keys';
import type { PendingToolApproval } from '../../../contracts/tool-approval.types';
import { resolveToolApproval } from '../../api/tool-approvals';
import type { ApprovalSteerStep } from '../lib/steerPendingApprovals';
import { orderApprovalsForSteer, planApprovalSteer } from '../lib/steerPendingApprovals';
import { clarificationAnswerDraftAtom } from '../state/clarificationAnswerDraftAtom';

/**
 * - ``partial``     the words landed, but a sibling's durable wait is still open.
 * - ``unavailable`` nothing resolvable — fall back to the ordinary send path.
 * - ``gone``        resolved out from under us; send via the ordinary path.
 * - ``held``        folded text landed in the draft; the wait is still open.
 */
export type SteerApprovalsOutcome =
  | 'steered'
  | 'partial'
  | 'unavailable'
  | 'gone'
  | 'failed'
  | 'held';

export type SteerPendingApprovals = (text: string) => Promise<SteerApprovalsOutcome>;

interface UseSteerPendingApprovalsArgs {
  agentRunId?: string;
  approvals: readonly PendingToolApproval[];
  /** The approval the chat card is presenting; it carries the user's words. */
  presentedApprovalId?: string;
}

const isAlreadyResolved = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 409;

const runStep = (step: ApprovalSteerStep) =>
  resolveToolApproval(step.approval, {
    verdict: step.verdict,
    reason: step.reason,
    modified_input: step.modifiedInput,
  });

/**
 * Resolves every pending approval on the run with the user's typed message. The
 * presented one goes FIRST and alone decides the outcome — if the user's words
 * didn't land there is nothing for the caller to clear.
 */
export const useSteerPendingApprovals = ({
  agentRunId,
  approvals,
  presentedApprovalId,
}: UseSteerPendingApprovalsArgs): SteerPendingApprovals => {
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useAtom(clarificationAnswerDraftAtom);

  return useCallback(
    async (text: string): Promise<SteerApprovalsOutcome> => {
      if (!agentRunId) return 'unavailable';

      const primaryId = orderApprovalsForSteer(approvals, presentedApprovalId)[0]
        ?.approvalId;
      const [primary, ...rest] = planApprovalSteer({
        approvals,
        presentedApprovalId,
        text,
        draft: primaryId ? (drafts[primaryId] ?? {}) : {},
      });
      if (!primary) return 'unavailable';

      if (primary.deferResolve) {
        setDrafts((prev) => ({
          ...prev,
          [primary.approval.approvalId]: primary.nextDraft ?? {},
        }));
        return 'held';
      }

      const dropDrafts = () => {
        setDrafts((prev) => {
          const next = { ...prev };
          delete next[primary.approval.approvalId];
          for (const step of rest) {
            delete next[step.approval.approvalId];
          }
          return next;
        });
      };

      try {
        const response = await runStep(primary);
        if (!response.accepted) {
          dropDrafts();
          return 'gone';
        }
      } catch (error) {
        if (isAlreadyResolved(error)) {
          dropDrafts();
          return 'gone';
        }
        // Never the error's text: it can carry PHI.
        logger.error('[approval-steer] resolve failed', {
          agent_run_id: agentRunId,
          approval_id: primary.approval.approvalId,
          verdict: primary.verdict,
          error: sanitizeError(error),
        });
        return 'failed';
      }

      dropDrafts();

      const settled = await Promise.allSettled(rest.map(runStep));

      let siblingLeftPending = false;
      settled.forEach((result, index) => {
        if (result.status === 'fulfilled') return;
        const error: unknown = result.reason;
        // A 409 is the desired end state, not a miss — that approval is already
        // resolved, so its wait is satisfied either way.
        if (isAlreadyResolved(error)) return;
        siblingLeftPending = true;
        logger.error('[approval-steer] superseded resolve failed', {
          agent_run_id: agentRunId,
          approval_id: rest[index]?.approval.approvalId,
          error: sanitizeError(error),
        });
      });

      void queryClient.invalidateQueries({ queryKey: inboxQueryKeys.all });
      // `hasPendingApproval` is mostly snapshot-derived, so a stale snapshot
      // mis-routes the next message: it steers into an approval list this steer
      // already emptied instead of taking the ordinary send path.
      void queryClient.invalidateQueries({
        queryKey: agentHarnessKeys.run(agentRunId),
      });
      return siblingLeftPending ? 'partial' : 'steered';
    },
    [agentRunId, approvals, drafts, presentedApprovalId, queryClient, setDrafts]
  );
};
